import { FC, PropsWithChildren, ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Close } from "@mui/icons-material";
import Button from "@mui/material/Button";
import variables from "../../styles/partials/_variables.module.scss";

type Props = {
  actionLabel: string;
  open: boolean;
  cancelActionLabel?: string;
  height?: string;
  isActionBtnDisabled?: boolean;
  modalContent?: ReactNode;
  padding?: string;
  title?: string;
  titleHelperText?: string;
  width?: string;
  onClose?: () => void;
  onSubmit?: () => void;
};

const Modal: FC<PropsWithChildren<Props>> = ({
  actionLabel,
  open,
  cancelActionLabel,
  height,
  isActionBtnDisabled,
  modalContent,
  padding,
  title = "Modal Title",
  titleHelperText,
  width,
  onClose,
  onSubmit,
}: PropsWithChildren<Props>) => (
  <Dialog
    aria-labelledby="customized-dialog-title"
    open={open}
    sx={{
      "& .MuiDialog-paper": { maxWidth: width, height }, // to override the default max-width of 600px
      "& .MuiPaper-root": {
        padding,
        width,
      },
    }}
    transitionDuration={0}
    onClose={onClose}
  >
    {/* The icon for closing the modal */}
    {onClose && (
      <IconButton
        TouchRippleProps={{ style: { color: "none" } }}
        aria-label="close"
        disableTouchRipple
        sx={{
          padding: ".75rem",
          position: "absolute",
          right: 16,
          top: 16,
          "&:hover": {
            backgroundColor: variables.backgroundGray,
          },
          "&:focus-visible": {
            borderRadius: "8px",
            outline: "2px solid none",
          },
        }}
        onClick={onClose}
      >
        <Close />
      </IconButton>
    )}

    {/* The modal's title */}
    <Typography id="customized-dialog-title" variant="h2">
      {title}
    </Typography>

    <Typography
      sx={{
        display: titleHelperText ? "" : "none",
        color: variables.paragraphPlaceholder,
        letterSpacing: "0.004em",
        padding: "0.5rem 0 0 0",
        textAlign: "left",
      }}
      variant="body1"
    >
      {titleHelperText}
    </Typography>

    {/* The modal's main content */}
    {modalContent && (
      <DialogContent sx={{ overflow: "visible", padding: "2rem 0 0 0" }}>
        {modalContent}
      </DialogContent>
    )}

    {/* The modal's action buttons */}
    <DialogActions>
      <div style={{ marginTop: "2.5rem" }}>
        {cancelActionLabel && (
          <Button type="button" onClick={onClose} variant="outlined">
            {cancelActionLabel}
          </Button>
        )}
        <Button
          disabled={isActionBtnDisabled}
          disableElevation
          sx={{ marginLeft: "1rem", padding: "8px 16px" }}
          type="button"
          variant="contained"
          onClick={onSubmit}
        >
          {actionLabel}
        </Button>
      </div>
    </DialogActions>
  </Dialog>
);

export default Modal;
